import React, { useState } from 'react';
import { Column, Table } from '@tanstack/react-table';
import { Button, Checkbox, Divider, FormControlLabel, Menu, MenuItem, Stack } from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { useTanstackTableLocale } from './locale';

interface ColumnVisibilityMenuProps<TData> {
  table: Table<TData>;
}

/** Human-readable label for a column: `meta.label`, else a string header, else id. */
function columnLabel<TData>(column: Column<TData, unknown>): string {
  return (
    column.columnDef.meta?.label ??
    (typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id)
  );
}

function ColumnVisibilityMenu<TData>({ table }: ColumnVisibilityMenuProps<TData>): JSX.Element {
  const locale = useTanstackTableLocale();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const hideableColumns = table
    .getAllLeafColumns()
    .filter((column: Column<TData, unknown>) => column.getCanHide());

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ViewColumnIcon />}
        onClick={(e: React.MouseEvent<HTMLElement>): void => setAnchor(e.currentTarget)}
      >
        {locale.columnsButton}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={(): void => setAnchor(null)}>
        <MenuItem dense>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={table.getIsAllColumnsVisible()}
                indeterminate={!table.getIsAllColumnsVisible() && table.getIsSomeColumnsVisible()}
                onChange={table.getToggleAllColumnsVisibilityHandler()}
              />
            }
            label={locale.toggleAll}
          />
        </MenuItem>
        <Divider />
        <Stack sx={{ maxHeight: 320, overflowY: 'auto' }}>
          {hideableColumns.map((column: Column<TData, unknown>) => (
            <MenuItem key={column.id} dense>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={column.getIsVisible()}
                    onChange={column.getToggleVisibilityHandler()}
                  />
                }
                label={columnLabel(column)}
              />
            </MenuItem>
          ))}
        </Stack>
      </Menu>
    </>
  );
}

export default ColumnVisibilityMenu;
